import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";

// One-time session exchange page. Reads #session_id=… from URL hash,
// swaps it for our JWT+cookie via POST /api/auth/google/session, then redirects.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const nav = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    const sessionId = m ? decodeURIComponent(m[1]) : null;
    if (!sessionId) {
      nav("/login", { replace: true });
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sessionId });
        login(data);
        // Strip the hash so nothing sensitive persists in history
        window.history.replaceState(null, "", window.location.pathname);
        toast.success(`Welcome, ${data.user?.name?.split(" ")[0] || "back"}!`);
        nav("/dashboard", { replace: true });
      } catch (e) {
        const msg = e?.response?.data?.detail || "Google sign-in failed. Please try again.";
        toast.error(msg);
        window.history.replaceState(null, "", window.location.pathname);
        nav("/login", { replace: true });
      }
    })();
  }, [location.hash, login, nav]);

  return (
    <div className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center px-6 py-10 text-center" data-testid="auth-callback">
      <Loader2 className="mb-4 h-8 w-8 animate-spin text-sarthi-primary" />
      <h1 className="font-display text-xl font-extrabold text-sarthi-text">Signing you in…</h1>
      <p className="mt-1 text-sm font-medium text-sarthi-muted">Please wait while we finish setting up your account.</p>
    </div>
  );
}
